import React, { Component } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { get, noop, isEqual, mapValues } from 'lodash-es';
import { connect } from 'react-redux';
import { ButtonGroup, Row, Col } from 'reactstrap';

// Models
import { bindToPath, connectToModel } from 'client/data/luckdragon/redux/react-binding';
import { ExperimentUtil } from 'client/utils/experiment/experiment-util';
import { MultiOfferModel, searchByVinPath } from 'client/data/models/multi-offer';

// Utils
import { getQuery } from 'client/utils/location';
import { EventToolbox } from 'client/utils/event-toolbox';
import { transformVin } from 'site-modules/shared/utils/appraiser/appraiser-validation-helper';
import { fireQuoteRecencyTrackingPixel } from 'site-modules/shared/utils/appraisal/common-utils';
import { delayHeavyTask } from 'client/site-modules/shared/utils/inp-utils';
import { Storage } from 'site-modules/shared/utils/storage';
import { getActiveTabId } from 'site-modules/shared/components/appraisal/appraisal-tabs-v2/utils/get-active-tab-id';
import { initializeTabConfiguration } from 'site-modules/shared/components/appraisal/appraisal-tabs-v2/utils/tab-initialization';

// Constants
import { APPRAISE_BY_TAB_HEADERS_CLASSNAME } from 'site-modules/shared/components/appraisal/appraisal-tabs/constants';
import {
  APPRAISAL_VEHICLE_ENTRY_CREATIVE_ID,
  APPRAISAL_VEHICLE_ENTRY_VIN_LOOKUP_CREATIVE_ID,
  PARTNER_OFFER_ADDRESS_LOOKUP,
} from 'client/site-modules/shared/constants/appraisal/appraisal';
import { TrackingConstant } from 'client/tracking/constant';
import {
  MMY_TAB_ID,
  VIN_TAB_ID,
  LICENSE_PLATE_TAB_ID,
  ADDRESS_TAB_ID,
  MMY_TAB,
  VIN_TAB,
  LICENSE_PLATE_TAB,
  ADDRESS_TAB,
  TABS,
  TAB_LINKS_ORDER_BY,
  DEFAULT_INITIAL_ACTIVE_TAB_ID,
} from 'site-modules/shared/components/appraisal/appraisal-tabs-container/appraisal-tab-constants';
import { APPRAISAL_STORAGE_KEYS } from 'site-modules/shared/components/appraisal/trade-in-appraiser-steps/license-plate-and-vin-step/constants';

// Components
import { AppraiseByMmyTabContainer } from 'site-modules/shared/components/appraisal/appraisal-tabs/appraise-by-mmy-tab-container';
import { VinTabContainer } from 'site-modules/shared/components/appraisal/appraisal-tabs/vin-tab/vin-tab-container';
import { AddressTab } from 'site-modules/shared/components/appraisal/appraisal-tabs/address-tab/address-tab';
import { LicensePlateTabContainer } from 'site-modules/shared/components/appraisal/appraisal-tabs/license-plate-tab/license-plate-tab-container';
import { AppraisalUtmStorage } from 'site-modules/shared/components/appraisal/appraisal-utm-storage/appraisal-utm-storage';
import { AppraisalFindVinDrawer } from 'site-modules/shared/components/find-vin-modal/appraisal-find-vin-drawer';
import { PrivatePartySellCar } from 'site-modules/shared/components/private-party/private-party-sell-car/private-party-sell-car';
import { SellingMethodsModule } from 'site-modules/shared/components/appraisal/selling-methods-module/selling-methods-module';
import { AppraiseByTabsHeaderItem } from './shared/appraise-by-tabs-header-item';
import { TabsCopy } from './shared/tabs-copy';
import { TabsContent } from './shared/tab-content';

import './appraisal-tabs.scss';

const MMY_TAB_WITH_COMPONENT = {
  ...MMY_TAB,
  TabComponent: AppraiseByMmyTabContainer,
};
const VIN_TAB_WITH_COMPONENT = {
  ...VIN_TAB,
  TabComponent: VinTabContainer,
};
const LICENSE_PLATE_TAB_WITH_COMPONENT = {
  ...LICENSE_PLATE_TAB,
  TabComponent: LicensePlateTabContainer,
};
const ADDRESS_TAB_WITH_COMPONENT = {
  ...ADDRESS_TAB,
  TabComponent: AddressTab,
};
const TABS_WITH_COMPONENT_BY_ID = {
  [MMY_TAB_ID]: MMY_TAB_WITH_COMPONENT,
  [VIN_TAB_ID]: VIN_TAB_WITH_COMPONENT,
  [LICENSE_PLATE_TAB_ID]: LICENSE_PLATE_TAB_WITH_COMPONENT,
};

const TABS_WITH_COMPONENT = mapValues(TABS, tabs => tabs.map(({ id }) => TABS_WITH_COMPONENT_BY_ID[id]));

const TAB_LINKS_ORDER_BY_WITH_COMPONENT = mapValues(TAB_LINKS_ORDER_BY, ({ FIRST, SECOND }) => ({
  FIRST: TABS_WITH_COMPONENT_BY_ID[FIRST.id],
  SECOND: TABS_WITH_COMPONENT_BY_ID[SECOND.id],
}));

const VIN_LOOKUP_BTN_CTA = 'VIN Lookup';

export class AppraisalTabsUI extends Component {
  static propTypes = {
    isMobile: PropTypes.bool,
    isLookUpStyleView: PropTypes.bool,
    isSellCar: PropTypes.bool,
    creativeId: PropTypes.string,
    ctaText: PropTypes.string,
    ctaClassName: PropTypes.string,
    isLicensePlateTabDefault: PropTypes.bool,
    location: PropTypes.shape({}),
    referer: PropTypes.string,
    bgClassName: PropTypes.string,
    className: PropTypes.string,
    title: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
    subtitle: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
    initialActiveTabId: PropTypes.string,
    isVdpEmbedded: PropTypes.bool,
    isYearDropdownFirst: PropTypes.bool,
    initialStateCode: PropTypes.string,
    initialVehicle: PropTypes.shape({
      make: PropTypes.string,
      model: PropTypes.string,
      year: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    }),
    setStylesAndVin: PropTypes.func,
    isFullWidth: PropTypes.bool,
    isVisibleInDrawer: PropTypes.bool,
    isProfileOfferVinPrepopulationEnabled: PropTypes.bool,
    profileOfferVin: PropTypes.string,
    hideEdmundsLogo: PropTypes.bool,
    hasAddressToVinDrawer: PropTypes.bool,
    isBackgroundStyleTabEnabled: PropTypes.bool,
    wrapperClassName: PropTypes.string,
    isEmoSearchEnabled: PropTypes.bool,
    isSameHeight: PropTypes.bool,
    vinModsData: PropTypes.shape({}),
    withContainerPadding: PropTypes.bool,
    isAppraisalLanding: PropTypes.bool,
    isSellCarLanding: PropTypes.bool,
    isSellInCityState: PropTypes.bool,
    linksColorClassName: PropTypes.string,
    accentColorClassName: PropTypes.string,
    accentBgColorClassName: PropTypes.string,
    isWIDG1125chal1: PropTypes.bool,
    isProjectEveStyle: PropTypes.bool,
    renderAddressTab: PropTypes.bool,
    setMmy: PropTypes.func.isRequired,
    setStyleIds: PropTypes.func.isRequired,
    setIsReadyForRedirect: PropTypes.func.isRequired,
    setVin: PropTypes.func.isRequired,
    storage: PropTypes.shape({}),
    setModelValue: PropTypes.func,
    ted7310Chal: PropTypes.string,
  };

  static defaultProps = {
    isMobile: false,
    isLookUpStyleView: false,
    isSellCar: false,
    isVdpEmbedded: false,
    isYearDropdownFirst: true,
    creativeId: APPRAISAL_VEHICLE_ENTRY_CREATIVE_ID,
    ctaText: "See your car's value",
    ctaClassName: undefined,
    isLicensePlateTabDefault: false,
    location: {},
    referer: undefined,
    title: '',
    subtitle: '',
    initialStateCode: '',
    bgClassName: 'bg-white',
    className: '',
    initialActiveTabId: DEFAULT_INITIAL_ACTIVE_TAB_ID,
    initialVehicle: null,
    setStylesAndVin: noop,
    isFullWidth: false,
    isVisibleInDrawer: false,
    isProfileOfferVinPrepopulationEnabled: false,
    profileOfferVin: '',
    hideEdmundsLogo: false,
    isAppExtensionPage: false,
    hasAddressToVinDrawer: false,
    isBackgroundStyleTabEnabled: false,
    wrapperClassName: '',
    isEmoSearchEnabled: false,
    isSameHeight: true,
    vinModsData: null,
    withContainerPadding: true,
    isAppraisalLanding: false,
    isSellCarLanding: false,
    isSellInCityState: false,
    linksColorClassName: 'text-primary-darker',
    accentColorClassName: 'text-primary-darker',
    accentBgColorClassName: 'bg-primary-opacity-2',
    isWIDG1125chal1: false,
    isProjectEveStyle: false,
    renderAddressTab: false,
    storage: null,
    setModelValue: noop,
    ted7310Chal: 'ctrl',
  };

  constructor(props) {
    super(props);
    this.state = this.buildTabsFromProps();
    AppraisalUtmStorage.checkAndSetSemCampaignAppraisals(this.props.location);
    AppraisalUtmStorage.checkAndSetSemCampaignFacebook(this.props.location);
  }

  componentDidMount() {
    const { tabs, activeTabId } = this.state;
    const { isLookUpStyleView, storage: storageFromProps } = this.props;

    if (!isLookUpStyleView) {
      this.fireTabTracking(tabs, activeTabId);
    }

    window.addEventListener('beforeunload', this.clearStorage);

    if (storageFromProps) {
      const savedActiveTabId = storageFromProps.get(APPRAISAL_STORAGE_KEYS.APPRAISAL_TABS_ACTIVE_TAB);

      if (savedActiveTabId) {
        this.setActiveTab(savedActiveTabId);
      }
    }
  }

  componentDidUpdate(prevProps) {
    const { tabs, activeTabId } = this.state;
    const { isVisibleInDrawer, isProfileOfferVinPrepopulationEnabled, profileOfferVin, vinModsData } = this.props;

    if (isVisibleInDrawer && !isEqual(prevProps.isVisibleInDrawer, isVisibleInDrawer)) {
      this.fireTabTracking(tabs, activeTabId);
    }

    if (isProfileOfferVinPrepopulationEnabled && prevProps.profileOfferVin !== profileOfferVin && profileOfferVin) {
      this.setActiveTab(VIN_TAB_ID);
    }

    if (!isEqual(vinModsData, prevProps.vinModsData)) {
      fireQuoteRecencyTrackingPixel(get(vinModsData, 'kmx.offersFinalizedAt'));
    }
  }

  componentWillUnmount() {
    window.removeEventListener('beforeunload', this.clearStorage);
  }

  /**
   * Toggles selected tab.
   * @param {string} tabId
   */
  setActiveTab = tabId =>
    delayHeavyTask(() => {
      this.setState({
        activeTabId: tabId,
      });

      this.storage.set(APPRAISAL_STORAGE_KEYS.APPRAISAL_TABS_ACTIVE_TAB, tabId);
    });

  getEdmundsLogo = () => (
    <div>
      <div className="d-inline-flex p-0_25 align-items-center text-blue-50 mb-0_25 bg-transparent size-16 fw-normal">
        <i className="icon-edmunds-car size-30 me-0_25" aria-hidden />
        <span className="ps-0_5">Did you know?</span>
      </div>
    </div>
  );

  storage = this.props.storage || new Storage('sessionStorage');

  clearStorage = () => {
    // Clear only the component-level storage
    if (!this.props.storage) {
      this.storage.remove(APPRAISAL_STORAGE_KEYS.LICENSE_PLATE);
      this.storage.remove(APPRAISAL_STORAGE_KEYS.STATE_CODE);
      this.storage.remove(APPRAISAL_STORAGE_KEYS.VIN);
      this.storage.remove(APPRAISAL_STORAGE_KEYS.APPRAISAL_TABS_ACTIVE_TAB);
    }
  };

  buildTabsFromProps = () => {
    const {
      location,
      isSellCar,
      referer,
      isLicensePlateTabDefault,
      initialActiveTabId,
      isLookUpStyleView,
      renderAddressTab,
    } = this.props;
    const { make, model, year, vin, tab } = getQuery(location);
    const hasMMYQuery = make && model && year; // Only used by Sell Car page: See EVAL-2352
    const hasVinQuery = !!vin;

    const { isLicensePlateTabDefaulted } = initializeTabConfiguration({
      tab,
      isLicensePlateTabDefault,
      isSellCar,
      referer,
    });

    let tabs = isLicensePlateTabDefaulted ? TABS_WITH_COMPONENT.LICENSE_PLATE : TABS_WITH_COMPONENT.DEFAULT;

    if (isLookUpStyleView) {
      tabs = !isLicensePlateTabDefault
        ? [...TABS_WITH_COMPONENT.YMM_SUPPRESSED].reverse()
        : TABS_WITH_COMPONENT.YMM_SUPPRESSED;
    }

    if (renderAddressTab) {
      tabs = [...tabs, ADDRESS_TAB_WITH_COMPONENT];
    }

    return {
      addressVin: null,
      tabs,
      prepopulatedVehicle: undefined,
      activeTabId: getActiveTabId({
        isLicensePlateTabDefault: isLicensePlateTabDefaulted,
        hasMMYQuery,
        hasVinQuery,
        isLookUpStyleView,
        initialActiveTabId,
      }),
      isPrivatePartyBtn: false,
    };
  };

  fireTabTracking = (tabs, activeTabId) => {
    EventToolbox.fireTrackAction({
      event_type: TrackingConstant.EVENT_TYPE_ACTION_COMPLETED,
      event_data: {
        action_category: TrackingConstant.SYSTEM_ACTION_CATEGORY,
        action_cause: TrackingConstant.PAGE_LOAD_CAUSE,
        action_name: TrackingConstant.ACTION_SHOW_CONTENT,
        subaction_name: TrackingConstant.TAB_DEFAULT,
        creative_id: this.props.creativeId,
        value: tabs.find(({ id: tabId }) => tabId === activeTabId).label,
      },
    });
  };

  handleTabLinkClick = event => {
    const tabId = event.currentTarget.dataset.tabId;
    this.setActiveTab(tabId);
  };

  handleAddressVin = (addressVin, hasVinMismatchError, vinStyles, { make, model, year }) => {
    const { setMmy, setStyleIds, setIsReadyForRedirect, setVin } = this.props;

    setVin(transformVin(addressVin));
    setMmy({
      make: get(vinStyles, '[0].makeNiceId', make),
      model: get(vinStyles, '[0].modelNiceId', model),
      year: get(vinStyles, '[0].year', year),
    });
    setStyleIds(vinStyles?.map(vinStyle => vinStyle?.styleId));
    setIsReadyForRedirect(true);
  };

  handlePrepopulatedVehicle = prepopulatedVehicle => {
    this.setState(() => ({
      prepopulatedVehicle,
    }));
  };

  handleBtnViewChange = isPrivatePartyBtn => {
    this.setState(() => ({ isPrivatePartyBtn }));
  };

  handleVinLookupBtnClick = () => {
    this.setActiveTab(ADDRESS_TAB_ID);
    EventToolbox.fireTrackAction({
      event_type: TrackingConstant.EVENT_TYPE_ACTION_START,
      event_data: {
        action_category: TrackingConstant.USER_ACTION_CATEGORY,
        action_name: TrackingConstant.ACTION_TOOL_SELECT,
        subaction_name: TrackingConstant.TAB_SELECT,
        action_cause: TrackingConstant.ACTION_CAUSE_BUTTON_CLICK,
        creative_id: APPRAISAL_VEHICLE_ENTRY_VIN_LOOKUP_CREATIVE_ID,
        value: VIN_LOOKUP_BTN_CTA,
      },
    });
  };

  renderAddressToVinDrawer = (isBackgroundStyleTabEnabled, displayAsLink = false) => {
    const {
      creativeId,
      isMobile,
      setStylesAndVin,
      location,
      isEmoSearchEnabled,
      accentColorClassName,
      accentBgColorClassName,
      isProjectEveStyle,
      renderAddressTab,
    } = this.props;
    const { activeTabId, prepopulatedVehicle } = this.state;
    const query = getQuery(location);

    if (renderAddressTab) return null;

    return (
      <AppraisalFindVinDrawer
        activeTabId={activeTabId}
        isMobile={isMobile}
        creativeId={PARTNER_OFFER_ADDRESS_LOOKUP}
        updateVinAndContinue={this.handleAddressVin}
        isLandingPage
        isBackgroundStyleTabEnabled={isBackgroundStyleTabEnabled || isProjectEveStyle}
        className={displayAsLink ? 'mb-1 size-16' : 'mt-0_25 size-14'}
        displayAsLink={displayAsLink}
        vehicle={{
          make: { slug: prepopulatedVehicle?.makeSlug },
          model: { slug: prepopulatedVehicle?.modelSlug },
          year: { year: prepopulatedVehicle?.year },
        }}
        openCtaAsButtonClassName={classnames(
          {
            'ms-lg-0_5 mt-lg-0': isEmoSearchEnabled,
          },
          accentBgColorClassName
        )}
        iconClassName={accentColorClassName}
        licensePlateAndVinForm={
          <LicensePlateTabContainer
            storage={this.storage}
            isMobile={isMobile}
            creativeId={creativeId}
            ctaText="Continue"
            query={query}
            setStylesAndVin={setStylesAndVin}
            isFullWidth
            isVinByAddressOverlay
          />
        }
      />
    );
  };

  render() {
    const {
      creativeId,
      bgClassName,
      className,
      title,
      subtitle,
      isLookUpStyleView,
      withContainerPadding,
      hideEdmundsLogo,
      isBackgroundStyleTabEnabled,
      wrapperClassName,
      isEmoSearchEnabled,
      isSameHeight,
      isSellCarLanding,
      isWIDG1125chal1,
      renderAddressTab,
      hasAddressToVinDrawer,
      isAppraisalLanding,
      linksColorClassName,
      accentColorClassName,
      accentBgColorClassName,
      isMobile,
      isSellCar,
      isSellInCityState,
      ctaText,
      ctaClassName,
      location,
      isVdpEmbedded,
      isYearDropdownFirst,
      initialStateCode,
      initialVehicle,
      setStylesAndVin,
      isFullWidth,
      isProfileOfferVinPrepopulationEnabled,
      profileOfferVin,
      setModelValue,
      ted7310Chal,
    } = this.props;
    const { tabs, activeTabId, isPrivatePartyBtn } = this.state;
    const isAddressTabActive = activeTabId === ADDRESS_TAB_ID;
    const showSellCarModule = ted7310Chal === 'chal-1' && isSellCarLanding;
    const showSellingMethodModule =
      ted7310Chal === 'chal-2' && isSellCarLanding && [LICENSE_PLATE_TAB_ID, VIN_TAB_ID].includes(activeTabId);

    const tabsCopyProps = {
      activeTabId,
      tabLinks: TAB_LINKS_ORDER_BY_WITH_COMPONENT[activeTabId],
      onTabLinkClick: this.handleTabLinkClick,
      onVinLookupBtnClick: this.handleVinLookupBtnClick,
      addressToVinDrawer: hasAddressToVinDrawer ? this.renderAddressToVinDrawer(true) : null,
      hasAddressToVinDrawer,
      renderAddressTab,
      isEmoSearchEnabled,
      isAppraisalLanding,
      isSellCarLanding,
      linksColorClassName,
      accentColorClassName,
      accentBgColorClassName,
    };

    const tabsContentProps = {
      tabs,
      activeTabId,
      storage: this.storage,
      onTabLinkClick: this.handleTabLinkClick,
      onPrepopulatedVehicle: this.handlePrepopulatedVehicle,
      onAddressVin: this.handleAddressVin,
      isMobile,
      isSellCar,
      isSellInCityState,
      isWIDG1125chal1,
      creativeId,
      ctaText,
      ctaClassName,
      location,
      isVdpEmbedded,
      isYearDropdownFirst,
      initialStateCode,
      initialVehicle,
      setStylesAndVin,
      isLookUpStyleView,
      isFullWidth,
      isProfileOfferVinPrepopulationEnabled,
      profileOfferVin,
      hasAddressToVinDrawer,
      isBackgroundStyleTabEnabled,
      isEmoSearchEnabled,
      isAppraisalLanding,
      setModelValue,
      isPrivatePartyBtn,
    };

    return (
      <div
        className={classnames('appraisal-tabs-container-wrapper', wrapperClassName, {
          'appraisal-tabs-container-wrapper-background-style-enabled': isBackgroundStyleTabEnabled,
          'is-same-height': isSameHeight,
          'has-address-tab': renderAddressTab,
        })}
        data-tracking-parent={creativeId}
      >
        {isBackgroundStyleTabEnabled && (
          <ButtonGroup size="md" role="tablist" className="tab-list">
            {tabs.map(({ id, label }, index) => (
              <AppraiseByTabsHeaderItem
                key={id}
                label={label}
                tabId={id}
                isActive={id === activeTabId}
                onBtnClick={this.setActiveTab}
                isFirstItem={index === 0}
                isBackgroundStyleTabEnabled
                renderAddressTab={renderAddressTab}
              />
            ))}
          </ButtonGroup>
        )}
        <div
          id="appraisal-tabs-container"
          className={classnames('appraisal-tabs-container', bgClassName, className, {
            rounded: !isLookUpStyleView && withContainerPadding,
            'is-emo-search-sell-car-landing': isEmoSearchEnabled && isSellCarLanding,
            'pb-lg-0_5': isSellCarLanding,
            'd-flex flex-column': ted7310Chal === 'chal-1',
          })}
          role={isBackgroundStyleTabEnabled ? 'tabpanel' : undefined}
        >
          {showSellingMethodModule && <SellingMethodsModule handleBtnViewChange={this.handleBtnViewChange} />}
          <div
            className={classnames({
              'p-1 p-md-1_5': !isLookUpStyleView && withContainerPadding,
            })}
          >
            {title && !isAddressTabActive && (
              <div className={classnames('heading-4', { 'mb-1_5': !isEmoSearchEnabled, 'mb-0_5': isEmoSearchEnabled })}>
                {title}
              </div>
            )}
            {subtitle && !isAddressTabActive && <div className="mb-1">{subtitle}</div>}
            <div
              className={classnames(`d-flex ${APPRAISE_BY_TAB_HEADERS_CLASSNAME}`, {
                'mb-1 mb-lg-1_5': isLookUpStyleView,
              })}
            >
              {isLookUpStyleView && (
                <span className="medium text-gray-darker fw-bold d-inline-block pb-0_5 me-0_5">Enter by:</span>
              )}
              {!isBackgroundStyleTabEnabled && (
                <ButtonGroup
                  size="md"
                  role="tablist"
                  className={classnames('tabs-button-list', {
                    'with-address-tab': renderAddressTab,
                    'mb-1': isAddressTabActive,
                  })}
                >
                  {tabs.map(({ id, label }, index) => (
                    <AppraiseByTabsHeaderItem
                      key={id}
                      label={label}
                      tabId={id}
                      isActive={id === activeTabId}
                      onBtnClick={this.setActiveTab}
                      isFirstItem={index === 0}
                    />
                  ))}
                </ButtonGroup>
              )}
            </div>
            {!isEmoSearchEnabled && isBackgroundStyleTabEnabled && !isAddressTabActive && (
              <div className="appraisal-tab-link-copy text-gray-dark size-14 mb-1_5">
                <TabsCopy {...tabsCopyProps} />
              </div>
            )}
            {!isLookUpStyleView && !isBackgroundStyleTabEnabled && !isAddressTabActive ? (
              <Row>
                <Col xs={12} className="appraisal-tab-link-copy text-gray-dark size-14 mt-1 mb-1_5">
                  {!hideEdmundsLogo && this.getEdmundsLogo()}
                  <TabsCopy {...tabsCopyProps} />
                </Col>
              </Row>
            ) : null}
            <TabsContent {...tabsContentProps} />
            {isEmoSearchEnabled && isBackgroundStyleTabEnabled && !isAddressTabActive && (
              <Row>
                <Col xs={12} className="appraisal-tab-link-copy text-gray-darker size-14 mt-1">
                  <div
                    className={classnames(
                      'size-14 mb-1_5 d-lg-flex',
                      isSellCarLanding ? 'align-items-end' : 'align-items-center'
                    )}
                  >
                    <TabsCopy {...tabsCopyProps} />
                  </div>
                </Col>
              </Row>
            )}
          </div>
          {showSellCarModule && (
            <PrivatePartySellCar activeTabLabel={tabs.find(({ id }) => id === activeTabId)?.label} />
          )}
        </div>
      </div>
    );
  }
}

export const stateToPropsConfig = {
  vinModsData: bindToPath(({ vin }) => (vin ? searchByVinPath.build(vin) : null), MultiOfferModel),
};

export const mapStateToProps = state => ({
  ted7310Chal: ExperimentUtil.getForcedOrAssignedRecipeName({
    state,
    campaignName: 'TED-7310-Sell-Car-V2',
    defaultVal: 'ctrl',
  }),
});

export const AppraisalTabs = connect(mapStateToProps)(connectToModel(AppraisalTabsUI, stateToPropsConfig));
